/**
 * Release notes — the single source for "What's new in CampusHub <version>".
 *
 * Keyed by the marketing version (apps/web/package.json "version", the same
 * value CI stamps as VITE_APP_VERSION and the stores show). Written for the
 * people who use the app at school — teachers, parents, office staff — never
 * for developers: say what changed for them, in their words, one line each.
 *
 * Shown by the update sheet (components/app-update) in two places:
 *   • the native "Update now" screen, for the version on offer;
 *   • once after an update lands ("What's new"), for the version now running.
 *
 * When you bump the package version, add an entry here first — a unit test
 * fails if the running version has no notes.
 */
import type { UserRole } from "@/store/auth-store";

/** The 3D object icons in public/images/icons/3d (Fluent Emoji 3D, MIT). */
export type NoteIcon =
  | "memo" | "camera" | "bar-chart" | "diary" | "megaphone" | "hundred-points" | "books"
  | "spiral-calendar" | "tear-off-calendar" | "school" | "graduation-cap" | "check-mark"
  | "warning" | "chart-increasing" | "rocket" | "party-popper" | "sparkles" | "sunrise"
  | "trophy" | "mobile-phone" | "wrench";

export interface ReleaseNote {
  icon: NoteIcon;
  /** A few words — what it is. */
  title: string;
  /** One sentence — what it does for you. */
  body: string;
  /** Only these roles see it; omitted → everyone. */
  roles?: readonly UserRole[];
}

export interface ReleaseEntry {
  /** "2.0.73" */
  version: string;
  /** YYYY-MM-DD (IST) the release went out. */
  date: string;
  /** The one-line summary under the title. */
  headline: string;
  notes: readonly ReleaseNote[];
}

const TEACHING: readonly UserRole[] = ["TEACHER"];

export const RELEASE_NOTES: Readonly<Record<string, ReleaseEntry>> = {
  "2.0.85": {
    version: "2.0.85",
    date: "2026-10-30",
    headline: "Report cards print the way the school asked: the new layout, and a Result only on the annual card.",
    notes: [
      { icon: "hundred-points", title: "Result only for the annual exam", body: "Report cards for formative, summative and mid-term assessments show marks and grades without a Result: PASS line; the annual card still shows it." },
      { icon: "memo", title: "Print Selected uses the new card", body: "Printing report cards for the students you tick now produces the same new-layout PDF as printing a whole class.", roles: ["TEACHER", "ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"] },
    ],
  },
  "2.0.84": {
    version: "2.0.84",
    date: "2026-10-27",
    headline: "The new CampusHubAI logo, and a sign-in screen that matches it.",
    notes: [
      { icon: "sparkles", title: "A fresh look", body: "The new CampusHubAI logo is on the sign-in screen, the side menu and your home-screen icon." },
      { icon: "mobile-phone", title: "Easier to read", body: "Sign-in and the public pages use clearer, stronger type, and you can pinch to zoom on the website." },
    ],
  },
  "2.0.83": {
    version: "2.0.83",
    date: "2026-10-26",
    headline: "An Old Dues tab, a Previous year due payment option, and thirty-four QA-board fixes.",
    notes: [
      { icon: "bar-chart", title: "Old dues in one place", body: "Finance has an Old Dues tab listing every balance from earlier years, and you can add one by hand or collect it straight from the list.", roles: ["ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"] },
      { icon: "check-mark", title: "Pay last year's balance on purpose", body: "Record Payment can send money straight to a student's previous year due, and refunds no longer show up as concessions." },
      { icon: "school", title: "Student records you can fix", body: "Joined date, admission details, section and the father, mother and guardian names can be edited, and Excel imports keep dates and mothers' names right." },
      { icon: "spiral-calendar", title: "Clearer attendance and exams for families", body: "Parents see full-day attendance when the school marks full days, hall tickets only once released, and only the exam types their child has." },
      { icon: "megaphone", title: "Simpler notices and grievances", body: "Notice board and announcements show only what parents and staff need, and complaints are filed under Grievances." },
    ],
  },
  "2.0.82": {
    version: "2.0.82",
    date: "2026-10-22",
    headline: "Forty-six QA-board fixes across fees, exams, notices and the phone app.",
    notes: [
      { icon: "check-mark", title: "Safer exam marks", body: "Marks below zero or above the paper's maximum are flagged and cannot be submitted or published, and a published exam's class stays fixed." },
      { icon: "bar-chart", title: "Approvals that reach the right people", body: "Refunds, large expenses and gradebook reviews now notify the approvers, and concession requests always carry a reason." },
      { icon: "mobile-phone", title: "More for families in the app", body: "Parents can see their child's counselling sessions and event tickets with the QR code, and student photos load on the home screen." },
      { icon: "sparkles", title: "Notices on your schedule", body: "Schedule a notice for later, set how long it stays up, and find taken-down notices again." },
      { icon: "warning", title: "Clearer screens", body: "The header avatar, lesson cards and report downloads behave on small phones, and the QA board is gone from the store app." },
    ],
  },
  "2.0.81": {
    version: "2.0.81",
    date: "2026-10-13",
    headline: "Thirteen QA-board fixes, refunds by fee head, and a quick way to rate the app.",
    notes: [
      { icon: "check-mark", title: "Refund the right fee", body: "A refund now lists the student's own fee heads, such as Tuition, IIT Fee or Transport, and the fee card, dues and reports follow." },
      { icon: "bar-chart", title: "Fees follow the year you pick", body: "Corrections, concessions and the boarding check show the academic year chosen at the top, and the dashboard shows the total due." },
      { icon: "mobile-phone", title: "Clearer app screens", body: "The attendance strips, subject labels and the Reports title read properly on the phone, and report downloads say where the file went." },
      { icon: "sparkles", title: "Enjoying CampusHub AI?", body: "After a good moment the app may ask for a store rating; Settings also has Rate the app." },
      { icon: "warning", title: "Safer sign-in", body: "Five wrong passwords lock sign-in for five minutes, even after a refresh; a one-time code still works." },
    ],
  },
  "2.0.80": {
    version: "2.0.80",
    date: "2026-10-07",
    headline: "Forty-four fixes from the QA board: attendance, fees, notices, meetings, timetable and more.",
    notes: [
      { icon: "check-mark", title: "Notices and messages behave", body: "A taken-down notice disappears for everyone, a new chat stays open after you pick someone, and the Acknowledge button is readable on every card." },
      { icon: "spiral-calendar", title: "Attendance you can browse", body: "Parents can step back through earlier months, leave days show as leave, and the register's Roll No. column shows only roll numbers." },
      { icon: "memo", title: "Print or download the timetable", body: "The week view has separate Print and Download buttons, and Print opens a real print dialog on Android." },
      { icon: "sparkles", title: "Quieter sign-out", body: "Signing out of the app stops push notifications for that account on the phone until you sign in again." },
    ],
  },
  "2.0.79": {
    version: "2.0.79",
    date: "2026-10-05",
    headline: "Cleaner forms and sheets, a menu that shows more, and a tidier gradebook.",
    notes: [
      { icon: "sparkles", title: "One clean look for every sheet", body: "Forms open with a compact header, and Cancel sits beside the main button instead of stacked above it." },
      { icon: "mobile-phone", title: "More of the menu at a glance", body: "The academic year now sits beside your role, so more modules fit on screen before you scroll." },
      { icon: "check-mark", title: "Save buttons clear the bottom bar", body: "Save Attendance and other action bars always sit above the tab bar, never under it." },
      { icon: "memo", title: "A tidier gradebook", body: "Students without a roll number show their admission number in full, without running under the marks." },
    ],
  },
  "2.0.78": {
    version: "2.0.78",
    date: "2026-10-04",
    headline: "A clearer bottom bar, tidier phone screens, and fee installments numbered the way your plan reads.",
    notes: [
      { icon: "mobile-phone", title: "A bottom bar you can see", body: "The tab bar now matches the navy top bar and sits a little lower, so it reads as the app's footer." },
      { icon: "sparkles", title: "Tidier phone screens", body: "Cards and sheets no longer squeeze words into a single column of letters on smaller phones." },
      { icon: "check-mark", title: "Installments in the right order", body: "After a switch to Boarding or a class change, the new plan's installments start at 1, as the fee structure says." },
      { icon: "memo", title: "A cleaner receipt header", body: "The fee receipt's header lines up the school's motto and contact details in one neat column." },
      { icon: "rocket", title: "Quicker fee lists", body: "The fee collection lists and reports open faster for large schools." },
    ],
  },
  "2.0.77": {
    version: "2.0.77",
    date: "2026-10-02",
    headline: "Cleaner hall tickets, a proper close of day at the fee counter, and a lighter app.",
    notes: [
      { icon: "memo", title: "Hall tickets, redesigned", body: "Three clean tickets per A4 sheet, printed with or without photos, with the exam instructions on each." },
      { icon: "check-mark", title: "Close the fee counter", body: "Sign off a counter's day with the cash counted; the day's receipts stay locked until an admin reopens it." },
      { icon: "bar-chart", title: "Fee figures that add up", body: "Every total, list and report reads from the same source, down to the paisa." },
      { icon: "rocket", title: "Smaller, faster app", body: "The app downloads lighter and opens quicker; nothing you use has been removed." },
    ],
  },
  "2.0.76": {
    version: "2.0.76",
    date: "2026-09-29",
    headline: "A fresh sign-in, steadier search and clearer fees.",
    notes: [
      { icon: "school", title: "New sign-in screen", body: "Our new CampusHub AI look, and the screen stays put when the keyboard opens." },
      { icon: "check-mark", title: "Search that stays open", body: "Search boxes no longer flicker or close the keyboard while you type." },
      { icon: "bar-chart", title: "Fees you can trust", body: "Pay towards tuition, transport or boarding, and see exactly what is due for each." },
    ],
  },
  "2.0.75": {
    version: "2.0.75",
    date: "2026-09-26",
    headline: "Meetings, rebuilt: video calls that feel like a real meeting room.",
    notes: [
      { icon: "camera", title: "New video call screen", body: "Gallery, speaker and presenter views, raise hand, reactions and chat right inside the call." },
      { icon: "check-mark", title: "Waiting room", body: "Hosts can let people in from a lobby, so only the right people join." },
      { icon: "spiral-calendar", title: "Your calendar, your meetings", body: "Meetings show only for the people invited, and move or disappear the moment they are rescheduled or cancelled." },
      { icon: "tear-off-calendar", title: "Accept or decline", body: "Reply Accept, Tentative or Decline from the calendar, and get a reminder 10 minutes before it starts." },
    ],
  },
  "2.0.74": {
    version: "2.0.74",
    date: "2026-09-26",
    headline: "A brand-new home screen, a morning welcome and a smoother day.",
    notes: [
      {
        icon: "memo",
        title: "Your new teacher home",
        body: "See the class you're in now, log a lesson in one tap, and keep your timetable, notices and tasks in one place.",
        roles: TEACHING,
      },
      {
        icon: "sunrise",
        title: "A daily welcome",
        body: "Each morning opens with a short brief — a thought for the day and what's on for you today.",
      },
      {
        icon: "trophy",
        title: "Your 360° performance",
        body: "See how you're doing across seven areas, read feedback and get simple next steps.",
        roles: TEACHING,
      },
      {
        icon: "camera",
        title: "Face check-in, more reliable",
        body: "Check-in now copes better with slow networks, busy mornings and camera hiccups.",
        roles: ["TEACHER", "STAFF", "ADMIN", "PRINCIPAL", "DIRECTOR", "CHAIRMAN"],
      },
      {
        icon: "bar-chart",
        title: "Gradebook fixed",
        body: "Your assessments show up again in the Gradebook, ready for marks.",
        roles: TEACHING,
      },
      {
        icon: "mobile-phone",
        title: "A tighter, cleaner app",
        body: "Every home screen now uses the full width of your phone, with less wasted space.",
      },
    ],
  },
};

/** The most we show at once — a sheet, not a changelog. */
export const MAX_NOTES = 6;

/**
 * The notes for `version`, filtered to what `role` should see. Null when the
 * version has no entry, or when nothing in it applies to this role.
 */
export function releaseNotesFor(version: string, role?: UserRole | null): ReleaseEntry | null {
  const entry = RELEASE_NOTES[version.trim()];
  if (!entry) return null;
  const notes = entry.notes
    .filter((n) => !n.roles || (role != null && n.roles.includes(role)))
    .slice(0, MAX_NOTES);
  return notes.length > 0 ? { ...entry, notes } : null;
}

/* ─── "What's new" — once per version ─────────────────────────────────────── */

const SEEN_KEY = "ch.whats-new.seen-version";

/**
 * Should "What's new" open for the version now running? Only after an
 * UPDATE: a first install records the version silently (a new user has
 * nothing to compare against), and each version is shown once.
 */
export function shouldShowWhatsNew(current: string, storage: Pick<Storage, "getItem" | "setItem"> | null): boolean {
  if (!storage) return false;
  let seen: string | null = null;
  try {
    seen = storage.getItem(SEEN_KEY);
  } catch {
    return false;
  }
  if (seen === current) return false;
  if (seen === null) {
    try { storage.setItem(SEEN_KEY, current); } catch { /* nothing to remember with */ }
    return false;
  }
  return true;
}

/** Record that the notes for `current` have been seen. */
export function markWhatsNewSeen(current: string, storage: Pick<Storage, "setItem"> | null): void {
  try { storage?.setItem(SEEN_KEY, current); } catch { /* per-device nicety only */ }
}
